import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { prescriptionAPI } from '@/lib/prescription-api';
import { queryKeys, invalidateQueries } from '@/lib/query-keys';
import type {
  CreateMedicineRequest,
  AddPrescriptionItemRequest,
  UpdatePrescriptionItemRequest
} from '@/types/prescription';

// --- Medicine Master Data Hooks ---

export const useMedicineSearch = (search: string, doseForm: string = '') => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.medicines(search, doseForm),
    queryFn: () => prescriptionAPI.searchMedicines(search, doseForm),
    enabled: search.length >= 2 || doseForm.length > 0, // Only search if at least 2 chars or doseForm selected
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useMedicineAPISearch = (search: string) => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.medicineAPIs(search),
    queryFn: () => prescriptionAPI.searchAPIs(search),
    enabled: search.length >= 2,
    staleTime: 1000 * 60 * 5,
  });
};

export const useMedicineUnits = () => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.medicineUnits(),
    queryFn: () => prescriptionAPI.getMedicineUnits(),
    staleTime: Infinity, // Unlikely to change often
  });
};

export const useMedicationDurations = () => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.medicineDurations(),
    queryFn: () => prescriptionAPI.getMedicationDurations(),
    staleTime: Infinity,
  });
};

export const useMedicationFrequencies = () => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.medicineFrequencies(),
    queryFn: () => prescriptionAPI.getMedicationFrequencies(),
    staleTime: Infinity,
  });
};

export const useDoseForms = () => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.doseForms(),
    queryFn: () => prescriptionAPI.getDoseForms(),
    staleTime: Infinity,
  });
};

// --- Prescription Management Hooks ---

export const usePrescriptions = (appointmentId: string, consolidated: boolean = false) => {
  return useQuery({
    queryKey: queryKeys.consultation.patientData.prescriptions(appointmentId, consolidated),
    queryFn: () => prescriptionAPI.getPrescriptions(appointmentId, consolidated),
    enabled: !!appointmentId,
  });
};

export const useCreateMedicine = () => {
  return useMutation({
    mutationFn: (data: CreateMedicineRequest) => prescriptionAPI.createMedicine(data),
  });
};

export const useAddPrescriptionItem = (appointmentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddPrescriptionItemRequest) => prescriptionAPI.addPrescriptionItem(appointmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(invalidateQueries.patientPrescriptions(appointmentId));
    },
  });
};

export const useUpdatePrescriptionItem = (appointmentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ prescriptionItemId, data }: { prescriptionItemId: string; data: UpdatePrescriptionItemRequest }) =>
      prescriptionAPI.updatePrescriptionItem(appointmentId, prescriptionItemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(invalidateQueries.patientPrescriptions(appointmentId));
    },
  });
};

export const useDeletePrescriptionItem = (appointmentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionItemId: string) => prescriptionAPI.deletePrescriptionItem(appointmentId, prescriptionItemId),
    onSuccess: () => {
      queryClient.invalidateQueries(invalidateQueries.patientPrescriptions(appointmentId));
    },
  });
};

